import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Button, Stack, Typography, useTheme, Box, Paper } from '@mui/material'
import ListIcon from '@mui/icons-material/List'
import MosSmartTable from '../../common/MosSmartTable'
import { widgetStyles } from '../../common/Widget'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import SingleJobModeBlockedSnackbar from '../../common/SingleJobModeBlockedSnackbar'

import JobStatusChangeDialog from './components/JobStatusChangeDialog'
import PlannedJobCustomModal from './components/PlannedJobCustomModal'
import PeriodSelect from './components/PeriodSelect'
import ComponentSelect from './components/ComponentSelect'
import JobDetailMos from './components/JobDetail'
import usePlannedJobs from './hooks/usePlannedJobs'
import useJobColumns from './hooks/useJobColumns'
import useBatchJobSelection, { BATCH_SELECTION_ROW_KEY_FIELD, BATCH_SELECTION_NOT_STARTABLE_FIELD } from './hooks/useBatchJobSelection'
import useMosInfo from '../../common/provider/useMosInfo'
import WorkerRequiredButton from '../../common/WorkerRequiredButton'
import BatchStartPreviewDialog from '../../common/batch/BatchStartPreviewDialog'
import BatchStartModeDialog from '../../common/batch/BatchStartModeDialog'
import BatchStartResultDialog from '../../common/batch/BatchStartResultDialog'
import BatchWarningDialog from '../../common/batch/BatchWarningDialog'
import BatchFinishDialog from '../../common/batch/BatchFinishDialog'
import { previewBatchStart, startBatch } from '../../common/batch/batchApi'
import { buildBatchStartRequest, BATCH_ACTIVE_STATUS_CODE } from '../../common/batch/batchContract'
import { resolveBatchWarningKind, BATCH_WARNING_KIND, hasEnoughGroupMembersToOfferShortcut, withholdBatchStartsInSingleJobMode } from '../../common/batch/batchWarning'
import { changeDialogInfo } from './utils/changeDialogInfo'
import { selectsJobOnSuccess } from './utils/jobStatus'
import { TAB_KEYS, PRIMARY_LIST_KEYS, NO_TECHNOLOGY_SELECTED_MESSAGE, getEffectivePrimaryList } from './utils/plannedJobTabs'
import useSharedVisibleTabs from './hooks/useSharedVisibleTabs'
import useSharedPlannedByTechnologyIds from './hooks/useSharedPlannedByTechnologyIds'
import usePlannedByTechnologyFetch from './hooks/usePlannedByTechnologyFetch'
import { PERIOD_KEYS } from './utils/periodFilter'
import { NO_COMPONENT } from './utils/componentFilter'
import usePlannedJobComponents from './hooks/usePlannedJobComponents'

// Historia 120 (Jupiter, paquete P5) -- S125/POL4/RN18: the pair `usePlannedJobs().fetchPlannedJobs`
// rows already carry (`fabricationOrder.id`, `jobReferenceId`), the exact shape N1/N2 want.
const batchRowKey = (fabricationOrderId, jobReferenceId) => `${fabricationOrderId}:${jobReferenceId}`

const CLOSED_BATCH_RESULT = { open: false, result: null, jobLabels: {}, rejection: null }

// Historia 120 — Bloque 12 (Jupiter, paquete P6): the three row-level warnings (group shortcut / batch
// resume / individual close of a batch member). `row` defaults to `{}` (CLAUDE.md: no nulls in
// business logic), never re-derived from the live queue once the warning is up.
const CLOSED_BATCH_WARNING = { open: false, kind: BATCH_WARNING_KIND.NONE, row: {}, statusCode: '', intent: '' }

// The GROUP_SHORTCUT/RESUME_BATCH "whole" choice funnels into the SAME preview screen as the picker
// flow (D-I10), but through its OWN state -- P5's `batchPreviewRequest`/`handleBatchPreviewConfirm`/
// `submitBatchStart` assume nothing about `selectionType` and stay untouched; this is a parallel,
// independent flow that only shares the rendered `<BatchStartPreviewDialog>`/`<BatchStartModeDialog>`
// elements (never two mounted at once -- only one gesture can be in flight).
const CLOSED_ROW_BATCH_FLOW = { open: false, request: {}, row: {} }
const EMPTY_PENDING_ROW_BATCH_START = { active: false, confirmedJobs: [], previewJobs: [], row: {} }
const CLOSED_ROW_BATCH_FINISH = { open: false, batchId: '' }

/**
 * Widget to display a list of planned jobs for the current device.
 *
 * @component
 */
const PlannedJobListWidget = (props = {}) => {
  const { preview = false, data, setData } = props
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const styles = widgetStyles(theme, 'primary')
  const { selectNode, worker, device, refreshActiveJobs, requestWorker, requestOpenQualityIncidence, globalData, setGlobalData } = useMosInfo()
  // Single Job Mode: the snackbar shown when the operator presses a withheld Start/Join (the button
  // keeps its label, looks disabled, and explains on click instead of starting).
  const [startBlockedOpen, setStartBlockedOpen] = useState(false)
  const handleStartBlocked = useCallback(() => { setStartBlockedOpen(true) }, [])
  const closeStartBlocked = useCallback(() => { setStartBlockedOpen(false) }, [])

  const hiddenColumnsInitRef = useRef(false)

  // Historia 120 (enmienda 05, RN8.9/RN12/S226) -- `submitBatchStart` is declared before
  // `useBatchJobSelection` becomes available (it needs `fetchPlannedJobs`, itself read from
  // `usePlannedJobs()` further down), so it calls the CURRENT reset function through this ref instead
  // of a forward reference. Wired once the hook is available, below.
  const resetBatchSelectionRef = useRef(() => {})

  useEffect(() => {
    if (hiddenColumnsInitRef.current) return
    hiddenColumnsInitRef.current = true
    if (globalData?.plannedJobListHiddenColumns === undefined) {
      setGlobalData((prev) => ({ ...prev, plannedJobListHiddenColumns: Array.isArray(data?.hiddenColumns) ? data.hiddenColumns : [] }))
    }
  }, [globalData, setGlobalData, data])

  useEffect(() => {
    const shared = globalData?.plannedJobListHiddenColumns
    if (!Array.isArray(shared)) return
    const current = Array.isArray(data?.hiddenColumns) ? data.hiddenColumns : []
    if (shared.length === current.length && shared.every((v, i) => v === current[i])) return
    if (setData) setData({ ...(data || {}), hiddenColumns: shared })
  }, [globalData?.plannedJobListHiddenColumns, data, setData])

  const effectiveHiddenColumns = Array.isArray(globalData?.plannedJobListHiddenColumns)
    ? globalData.plannedJobListHiddenColumns
    : data?.hiddenColumns

  // Historia 120 (Jupiter, paquete P5) -- S116-S128/S206: the previsualización -> mode question ->
  // N2 orchestration. ⚠️ §7.2 #6/S124: this does NOT go through `useJobStatusChange` (the flow of
  // ONE job, shared by six call sites) -- it is a separate flow, built here and nowhere else.
  //
  // `batchPickedRows` keeps the FULL rows the operator picked (as `usePlannedJobs().fetchPlannedJobs`
  // resolves them, with their real `node`/`fabricationOrder` objects) so the widget can resolve the
  // FIRST started job back to something `selectNode` accepts (S126), without a second lookup call --
  // N1/N2 responses only carry id pairs, never the row's own `node` object.
  const [batchPickedRows, setBatchPickedRows] = useState([])
  // Historia 120 (enmienda 05, D-SP23/S224) -- the PAIRS the gesture confirms, snapshotted at click
  // time from `batchSelection.selectedPairs` (parsed straight off every marked `rowKey`, RN8.5): a
  // marked pair whose row was never fetched (S224's "otra página") still has NO full row to recase,
  // but it must still travel to N1 -- `batchPickedRows` (FULL rows, used only for S126's best-effort
  // auto-select) would silently drop it.
  const [batchPickedPairs, setBatchPickedPairs] = useState([])
  const [batchPreviewOpen, setBatchPreviewOpen] = useState(false)
  const [batchModeOpen, setBatchModeOpen] = useState(false)
  // The confirmed list + the raw preview response, held ONLY while the mode question is on screen
  // (S121: the chosen mode is applied to exactly this list, never re-derived from the queue).
  const [pendingBatchStart, setPendingBatchStart] = useState(null)
  const [batchResult, setBatchResult] = useState(CLOSED_BATCH_RESULT)

  // Historia 120 (Jupiter, paquete P6) -- S129-S142: the three row-level warnings, resolved by the
  // ONE resolver (`common/batch/batchWarning.js`) at the exact derivation point `03-dominio.md` §7.2
  // #7/#8/#8-bis names (`useJobColumns.renderStatusActions` -> `onStatusChange(row, statusCode, intent)`).
  const [batchWarning, setBatchWarning] = useState(CLOSED_BATCH_WARNING)
  // The "whole group"/"whole batch" choice out of the warning above -- its OWN preview flow (own
  // request, own mode question, own submit), independent of the picker's (S125 comment above).
  const [rowBatchFlow, setRowBatchFlow] = useState(CLOSED_ROW_BATCH_FLOW)
  const [rowBatchModeOpen, setRowBatchModeOpen] = useState(false)
  const [pendingRowBatchStart, setPendingRowBatchStart] = useState(EMPTY_PENDING_ROW_BATCH_START)
  // S138: "finish the whole batch" is N4+N5, i.e. `BatchFinishDialog` (P8) mounted as-is -- no new
  // route, no second finish component.
  const [rowBatchFinish, setRowBatchFinish] = useState(CLOSED_ROW_BATCH_FINISH)

  // S125/POL4/RN18: built once, when the picker confirms -- never re-derived from the live queue
  // between the preview and the confirmation, so a queue change in between cannot expand or shrink
  // the gesture.
  const batchPreviewRequest = useMemo(() => ({
    deviceId: device?.id,
    workerId: worker?.id,
    selectionType: 'JOBS',
    jobs: batchPickedPairs
  }), [device?.id, worker?.id, batchPickedPairs])

  // S123: cancelling the preview altogether (not the mode question) drops the whole gesture -- no
  // request of any kind was ever sent.
  const handleBatchPreviewClose = useCallback(() => {
    setBatchPreviewOpen(false)
    setBatchModeOpen(false)
    setBatchPickedRows([])
    setBatchPickedPairs([])
    setPendingBatchStart(null)
  }, [])

  // N2 -- built with `buildBatchStartRequest` (P2), the one place that resolves `statusId` PER JOB by
  // code (S121/§8.2-bis) and keeps the job list deduplicated/non-empty (S179). Kept as one function so
  // both the "no mode needed" (S122) and the "mode chosen" (S121) paths call the exact same submit.
  const submitBatchStart = useCallback(async (confirmedJobs, previewJobs, chosenModeCode) => {
    const request = await buildBatchStartRequest({
      deviceId: device?.id,
      workerId: worker?.id,
      jobs: confirmedJobs,
      chosenModeCode
    })

    // S210/D-SP20/§7.2 #15-ter: N2's gesture-wide `400` (`BatchSelectionEmpty`/`BatchDuplicateJob`/
    // `BatchInvalidTargetStatus`/`JobNotInDeviceQueue`/`JobGovernedByAcceptance`/the external-circuit
    // literal) is best-effort's OTHER outcome -- nothing started, nothing left out, the whole gesture
    // never happened (RN18/POL4). It reuses the SAME sink/dialog as a normal result
    // (`BatchStartResultDialog`, P5), never a second dialog or a second translator: `error` is the
    // `{message}` `Api_Functions.jsx` already resolved, painted by the ONE shared translator
    // (`translateBatchRejectionMessage`), no whitelist of "expected" literals.
    let result
    try {
      result = await startBatch(request)
    } catch (error) {
      setBatchPreviewOpen(false)
      setBatchModeOpen(false)
      setPendingBatchStart(null)
      setBatchPickedRows([])
      setBatchPickedPairs([])
      setBatchResult({ open: true, result: null, jobLabels: {}, rejection: error })
      return
    }

    setBatchPreviewOpen(false)
    setBatchModeOpen(false)
    setPendingBatchStart(null)
    setBatchPickedRows([])
    setBatchPickedPairs([])

    // S127: `result` travels WHOLE to the result dialog -- `warnings`/`batchId` are never dropped by
    // picking only `started`/`notStarted` out of it here.
    const previewList = Array.isArray(previewJobs) ? previewJobs : []
    const jobLabels = Object.fromEntries(previewList.map((row) => [
      batchRowKey(row.fabricationOrderId, row.jobReferenceId),
      { fabricationOrderCode: row.fabricationOrderCode, partReferenceCode: row.partReferenceCode, nodeName: row.nodeName }
    ]))
    setBatchResult({ open: true, result, jobLabels, rejection: null })

    // S126: the table reloads and the station's active-jobs poll refreshes immediately (same pattern
    // as the other batch writes elsewhere in `Mos/Widgets`, e.g. `useJobModeTransition.js`), and the
    // FIRST started job (in the order the server returned) is left selected -- the station is never
    // left with nothing selected because of a batch start.
    setReloadFlag((prev) => prev + 1)
    if (refreshActiveJobs) refreshActiveJobs()

    const started = Array.isArray(result?.started) ? result.started : []
    // S226/RN8.9/RN12 -- the selection is a ONE-TIME act: it clears ENTIRE only once the gesture
    // actually occurred (at least one job started). Zero started (a `200` that started nobody) keeps
    // it intact -- nothing changed state, so there is nothing to punish the operator for.
    if (started.length > 0) resetBatchSelectionRef.current()
    const firstStarted = started[0]
    if (firstStarted) {
      const pickedRow = batchPickedRows.find((row) =>
        row?.fabricationOrder?.id === firstStarted.fabricationOrderId && row?.jobReferenceId === firstStarted.jobReferenceId)
      if (pickedRow) {
        const { node, fabricationOrderInfo, jobReference } = await changeDialogInfo({
          node: pickedRow.node,
          fabricationOrder: pickedRow.fabricationOrder,
          jobReference: { id: pickedRow.jobReferenceId }
        })
        selectNode(node, fabricationOrderInfo, jobReference)
      }
    }
  }, [device, worker, batchPickedRows, refreshActiveJobs, selectNode])

  // S120/S121/S122/S206: ONE question for the whole gesture, asked only when at least one CONFIRMED
  // job manages preparation -- computed fresh from what the operator actually confirmed (never from
  // N1's own `modeChoiceRequired`, which reflects the ORIGINAL preview, not a batch the operator later
  // reduced by unchecking rows, S206).
  const handleBatchPreviewConfirm = useCallback(({ confirmedJobs, previewJobs }) => {
    if (confirmedJobs.some((job) => job.managePreparation)) {
      setPendingBatchStart({ confirmedJobs, previewJobs })
      setBatchModeOpen(true)
      return
    }
    submitBatchStart(confirmedJobs, previewJobs, BATCH_ACTIVE_STATUS_CODE.EXECUTION)
  }, [submitBatchStart])

  // S123: cancelling the mode question sends NOTHING and returns to the (still mounted, still open)
  // preview dialog -- the operator's earlier selection is untouched because that dialog was never
  // closed in the first place.
  const handleBatchModeCancel = useCallback(() => {
    setBatchModeOpen(false)
  }, [])

  const handleBatchModeConfirm = useCallback((chosenModeCode) => {
    if (!pendingBatchStart) return
    submitBatchStart(pendingBatchStart.confirmedJobs, pendingBatchStart.previewJobs, chosenModeCode)
  }, [pendingBatchStart, submitBatchStart])

  const handleBatchResultClose = useCallback(() => {
    setBatchResult(CLOSED_BATCH_RESULT)
  }, [])

  const [statusChangeDialogInfo, setStatusChangeDialogInfo] = useState({ open: false })
  const [foInfoDialog, setFoInfoDialog] = useState({ open: false, fabricationOrder: null, node: null, jobReference: null })
  const [openCustomModal, setOpenCustomModal] = useState(false)
  // UX fix: remembers the modal's last selected tab across closing/reopening it. Lifted here (the
  // modal is always mounted, controlled purely via `open`, but this makes the persistence explicit
  // and independent of the modal's own mount/unmount lifecycle) as session-only UI state -- NOT
  // persisted to the widget config (widgets/device/{id}).
  const [modalTabIndex, setModalTabIndex] = useState(0)
  // Bumped after any successful status change to force an immediate table reload
  // (the polling interval would otherwise refresh with up to a one-second delay).
  const [reloadFlag, setReloadFlag] = useState(0)

  // Period select: narrows the main list by a time window (Today/This week/This month/This
  // year). Session-only UI state, not persisted, independent from the modal's own selection.
  const [period, setPeriod] = useState(PERIOD_KEYS.ALL)
  const periodInitRef = useRef(false)
  useEffect(() => {
    // Skip the very first render: only reload when the worker actually changes the period,
    // not on mount (the table already fetches once on its own).
    if (!periodInitRef.current) {
      periodInitRef.current = true
      return
    }
    setReloadFlag((prev) => prev + 1)
  }, [period])

  // Story 219 (RN9/RN10/RN11): the component this instance filters by -- its CODE, session-only like
  // the period, never in the widget config. Always offered, except in the edit-mode preview, which
  // has no header (and so asks no catalog).
  const [componentCode, setComponentCode] = useState(NO_COMPONENT)
  const componentOptions = usePlannedJobComponents({ enabled: !preview })

  const {
    fetchPlannedJobs,
    fetchAllPlannedJobs,
    fetchPlannedByTechnologyJobs,
    fetchCompatibleJobs,
    fetchFinishedCompatibleJobs,
    fetchAllActiveJobs,
    fetchReworkJobs,
    fetchFinishedInDeviceJobs,
    error
  } = usePlannedJobs(componentCode, period)

  // Historia 120 (enmienda 05, D-SP22/D-SP23) -- the batch selection that lives ABOVE the main list's
  // table: identified by the (fabricationOrderId, jobReferenceId) pair, materialised as the synthetic
  // `rowKey` this hook's own data wrapper adds (S222/S230), surviving pagination and the widget's own
  // automatic refresh (RN8.5/S223) until an explicit reset (RN8.9/RN12/S226, wired below).
  const batchSelection = useBatchJobSelection(fetchPlannedJobs)

  useEffect(() => {
    resetBatchSelectionRef.current = batchSelection.resetSelection
  }, [batchSelection.resetSelection])

  // S226 (P5 point closed 2026-09-13): the selection is also of the OPERATOR present and the MACHINE
  // queried -- it clears entire on either change, never on the very first render (there is nothing to
  // clear yet then). RN70/RN66.4: the fourth gate is evaluated with the operator's OWN identity, so a
  // batch marked by one operator and confirmed by another would be confusing (POL3/POL7: when in
  // doubt, the client does less).
  const previousDeviceIdRef = useRef(device?.id)
  const previousWorkerIdRef = useRef(worker?.id)
  useEffect(() => {
    const deviceChanged = previousDeviceIdRef.current !== device?.id
    const workerChanged = previousWorkerIdRef.current !== worker?.id
    previousDeviceIdRef.current = device?.id
    previousWorkerIdRef.current = worker?.id
    if (deviceChanged || workerChanged) batchSelection.resetSelection()
  }, [device?.id, worker?.id, batchSelection])

  // HU-04 (S16/S17/S18): which tabs are visible in the modal, with a retrocompatible default (RN10).
  // SHARED with the Operation widget through the MOS context, like the column visibility already
  // was: both open the same modal from the same configuration panel, so the decision is of the work
  // centre and not of the widget instance.
  const effectiveVisibleTabs = useSharedVisibleTabs(data, setData)
  // The main view stays per widget -- only this one has one, and an invalid primary list degrades
  // silently to the widget's own queue (E3/V3).
  const effectivePrimaryList = getEffectivePrimaryList(data?.primaryList, effectiveVisibleTabs)
  // Story 193 (MIC-4346, S41/S42): "All Jobs" as main list -- the work centre's technology selection
  // (shared with the Operation widget, RN6), the catalog wait and the message when nothing counts.
  const plannedByTechnologyIds = useSharedPlannedByTechnologyIds(data, setData)
  const reloadMainList = useCallback(() => { setReloadFlag((prev) => prev + 1) }, [])
  const { noSelection: noPlannedByTechnologySelection, dataFetch: fetchAllJobsMainList } = usePlannedByTechnologyFetch({
    plannedByTechnologyIds,
    enabled: effectivePrimaryList === TAB_KEYS.PLANNED_BY_TECHNOLOGY,
    fetchPlannedByTechnologyJobs,
    onSelectionChange: reloadMainList
  })
  const showNoTechnologyMessage = effectivePrimaryList === TAB_KEYS.PLANNED_BY_TECHNOLOGY && noPlannedByTechnologySelection
  const primaryFetchByKey = {
    [PRIMARY_LIST_KEYS.DEFAULT]: fetchPlannedJobs,
    [TAB_KEYS.ALL_PLANNED]: fetchAllPlannedJobs,
    [TAB_KEYS.COMPATIBLE]: fetchCompatibleJobs,
    [TAB_KEYS.FINISHED_COMPATIBLE]: fetchFinishedCompatibleJobs,
    [TAB_KEYS.ALL_ACTIVE]: fetchAllActiveJobs,
    [TAB_KEYS.REWORK]: fetchReworkJobs,
    // Story 122 (S58): needed even though the tab is opt-in, because the config popover offers one
    // "Main list" option per VISIBLE tab -- without this entry choosing it would leave
    // `primaryFetch` undefined and the widget's main view permanently empty. Chosen as the main
    // list it keeps its own server order (finish date descending) and its own period axis, both of
    // which live in the fetcher. (EXTERNAL_CIRCUIT is absent on purpose -- its columns are
    // visibility-only and it cannot feed the main list, which renders the shared `columns`.)
    [TAB_KEYS.FINISHED_IN_DEVICE]: fetchFinishedInDeviceJobs,
    // Story 193 (S41): offered as "Main list" once visible, so it needs its entry here too.
    [TAB_KEYS.PLANNED_BY_TECHNOLOGY]: fetchAllJobsMainList
  }
  const primaryFetch = primaryFetchByKey[effectivePrimaryList]

  // S108/RN7.1 (P1 closed 2026-09-13): the batch selection is offered ONLY when the main list IS the
  // planned queue -- with any OTHER list configured, this machine loses the capacity, and the loss is
  // declared (no checkboxes, no button), never a second universe from which to mark a queue the
  // operator is not looking at (D-I3).
  const isQueuePrimaryList = effectivePrimaryList === PRIMARY_LIST_KEYS.DEFAULT
  // Single Job Mode (published by MosScreen from the Operation widget's setting): a batch starts
  // several jobs at once, which is the very thing the mode forbids, so the whole batch capacity of
  // this widget (checkboxes, "Start batch", the group/batch shortcuts of a row) is withheld while it
  // is on -- declared, like with a non-queue primary list, never a second universe.
  const singleJobMode = globalData?.singleJobMode === true
  const batchSelectionActive = isQueuePrimaryList && !singleJobMode
  const mainListDataFetch = batchSelectionActive ? batchSelection.dataFetch : primaryFetch

  // The individual flow of today (start/finish/pause), UNCHANGED in shape -- this is what "choosing
  // only this job" out of any of the three warnings runs, and what a row with no group/no batch has
  // always run (no regression, D2).
  const openIndividualStatusChange = useCallback((row, statusCode, intent) => {
    setStatusChangeDialogInfo({
      open: true,
      node: row.node,
      fabricationOrder: row.fabricationOrder,
      jobReference: { id: row.jobReferenceId },
      statusCode,
      intent,
      // C8: the row is the ONLY valid source of the switch for a job that is not active anywhere
      // (by starting, paused or finished). Absent/null resolves to false here, not downstream, so
      // the dialog never decides on a missing value (RN61/POL5).
      managePreparation: row.managePreparation === true
    })
  }, [])

  // D-I8/R-9: N1 in `PLANNING_GROUP` mode -- the server counts, the client never does (S133/G1-9).
  const buildGroupShortcutRequest = useCallback((row) => ({
    deviceId: device?.id,
    workerId: worker?.id,
    selectionType: 'PLANNING_GROUP',
    planningGroup: row?.planningGroup,
    originJob: { fabricationOrderId: row?.fabricationOrder?.id, jobReferenceId: row?.jobReferenceId }
  }), [device, worker])

  // D-I5: "resume the whole batch" has no route of its own -- N1 in `BATCH` mode + N2, both already
  // built (P2).
  const buildBatchResumeRequest = useCallback((row) => ({
    deviceId: device?.id,
    workerId: worker?.id,
    selectionType: 'BATCH',
    batchId: row?.batchId
  }), [device, worker])

  // Historia 120 (Jupiter, paquete P6) -- S130/S134's "whole group"/"whole batch" choice: its OWN N2
  // submit, independent of `submitBatchStart` above (that one stays exactly P5's, untouched). Shares
  // the SAME result sink (`batchResult`/`BatchStartResultDialog`) on purpose: "el resultado deja un
  // identificador de lote, igual que cualquier inicio en lote" (S130) is presentation, not a route.
  const submitRowBatchStart = useCallback(async (confirmedJobs, previewJobs, chosenModeCode, originRow) => {
    const request = await buildBatchStartRequest({
      deviceId: device?.id,
      workerId: worker?.id,
      jobs: confirmedJobs,
      chosenModeCode
    })

    // S210: the SAME gesture-wide `400` handling as `submitBatchStart` above -- same sink, same
    // dialog, same translator, no whitelist.
    let result
    try {
      result = await startBatch(request)
    } catch (error) {
      setRowBatchFlow(CLOSED_ROW_BATCH_FLOW)
      setRowBatchModeOpen(false)
      setPendingRowBatchStart(EMPTY_PENDING_ROW_BATCH_START)
      setBatchResult({ open: true, result: null, jobLabels: {}, rejection: error })
      return
    }

    setRowBatchFlow(CLOSED_ROW_BATCH_FLOW)
    setRowBatchModeOpen(false)
    setPendingRowBatchStart(EMPTY_PENDING_ROW_BATCH_START)

    const previewList = Array.isArray(previewJobs) ? previewJobs : []
    const jobLabels = Object.fromEntries(previewList.map((previewRow) => [
      batchRowKey(previewRow.fabricationOrderId, previewRow.jobReferenceId),
      { fabricationOrderCode: previewRow.fabricationOrderCode, partReferenceCode: previewRow.partReferenceCode, nodeName: previewRow.nodeName }
    ]))
    setBatchResult({ open: true, result, jobLabels, rejection: null })

    setReloadFlag((prev) => prev + 1)
    if (refreshActiveJobs) refreshActiveJobs()

    // Best-effort auto-select, same degrade-gracefully pattern as `submitBatchStart`: only the ONE
    // row that triggered this gesture is a known candidate here (no full picker list to search).
    const started = Array.isArray(result?.started) ? result.started : []
    const firstStarted = started[0]
    if (firstStarted && originRow?.fabricationOrder?.id === firstStarted.fabricationOrderId && originRow?.jobReferenceId === firstStarted.jobReferenceId) {
      const { node, fabricationOrderInfo, jobReference } = await changeDialogInfo({
        node: originRow.node,
        fabricationOrder: originRow.fabricationOrder,
        jobReference: { id: originRow.jobReferenceId }
      })
      selectNode(node, fabricationOrderInfo, jobReference)
    }
  }, [device, worker, refreshActiveJobs, selectNode])

  // S123-equivalent for this flow: cancelling drops the whole gesture, nothing was ever sent.
  const handleRowBatchPreviewClose = useCallback(() => {
    setRowBatchFlow(CLOSED_ROW_BATCH_FLOW)
    setRowBatchModeOpen(false)
    setPendingRowBatchStart(EMPTY_PENDING_ROW_BATCH_START)
  }, [])

  const handleRowBatchPreviewConfirm = useCallback(({ confirmedJobs, previewJobs }) => {
    const originRow = rowBatchFlow.row
    if (confirmedJobs.some((job) => job.managePreparation)) {
      setPendingRowBatchStart({ active: true, confirmedJobs, previewJobs, row: originRow })
      setRowBatchModeOpen(true)
      return
    }
    submitRowBatchStart(confirmedJobs, previewJobs, BATCH_ACTIVE_STATUS_CODE.EXECUTION, originRow)
  }, [rowBatchFlow, submitRowBatchStart])

  const handleRowBatchModeCancel = useCallback(() => {
    setRowBatchModeOpen(false)
  }, [])

  const handleRowBatchModeConfirm = useCallback((chosenModeCode) => {
    if (!pendingRowBatchStart.active) return
    submitRowBatchStart(pendingRowBatchStart.confirmedJobs, pendingRowBatchStart.previewJobs, chosenModeCode, pendingRowBatchStart.row)
  }, [pendingRowBatchStart, submitRowBatchStart])

  // S138: "finish the whole batch" -- `BatchFinishDialog` (P8) as-is, no new route.
  const handleRowBatchFinishClose = useCallback(() => {
    setRowBatchFinish(CLOSED_ROW_BATCH_FINISH)
  }, [])

  const handleRowBatchFinished = useCallback(() => {
    setRowBatchFinish(CLOSED_ROW_BATCH_FINISH)
    setReloadFlag((prev) => prev + 1)
    if (refreshActiveJobs) refreshActiveJobs()
  }, [refreshActiveJobs])

  // Historia 120 (Jupiter, paquete P6) -- S129/S134/S136's "aviso no ha actuado todavía": dismissing
  // fires neither choice.
  const handleBatchWarningClose = useCallback(() => {
    setBatchWarning(CLOSED_BATCH_WARNING)
  }, [])

  // S131/S137: "only this job" -- the individual flow of today, with the SAME statusCode/intent the
  // operator originally clicked. No batch call of any kind.
  const handleBatchWarningChooseIndividual = useCallback(() => {
    const { row, statusCode, intent } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)
    openIndividualStatusChange(row, statusCode, intent)
  }, [batchWarning, openIndividualStatusChange])

  // S130/S134: "the whole group"/"the whole batch" -- the ONE preview screen (D-I10), varying only
  // `request.selectionType`. S138: "the whole batch" (finish) -- `BatchFinishDialog`, no new route.
  const handleBatchWarningChooseBatch = useCallback(() => {
    const { kind, row } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)

    if (kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setRowBatchFinish({ open: true, batchId: row?.batchId ?? '' })
      return
    }

    const request = kind === BATCH_WARNING_KIND.GROUP_SHORTCUT
      ? buildGroupShortcutRequest(row)
      : buildBatchResumeRequest(row)
    setRowBatchFlow({ open: true, request, row })
  }, [batchWarning, buildGroupShortcutRequest, buildBatchResumeRequest])

  // Story 113 (S114/S115, R-F): `intent` travels alongside `statusCode`. It is what decides the
  // auto-selection below and the hook's non-productive check -- neither can be derived from the code,
  // because a preparation -> execution transition sends exactly the code a start sends.
  //
  // Historia 120 (Jupiter, paquete P6) -- S129-S142: THE derivation point (`03-dominio.md` §7.2
  // #7/#8/#8-bis). The ONE resolver (`resolveBatchWarningKind`) decides synchronously for
  // RESUME_BATCH/FINISH_BATCH_MEMBER/NONE (no network needed -- `batchId`/`planningGroup` are already
  // on the row) and precedence rules out any group consideration whenever a batch is present
  // (S135/S140/INV10). GROUP_SHORTCUT is the one candidate that needs Sun's own count (D-I8/R-9) before
  // deciding whether to actually show anything (S129 vs S133) -- everything else here is the exact
  // synchronous path this widget has always had.
  const handleStatusChange = useCallback((row, statusCode, intent = '') => {
    if (!worker) {
      if (requestWorker) requestWorker()
      return
    }

    // Single Job Mode: the group shortcut and the batch resume would start SEVERAL jobs, so neither is
    // offered -- the gesture is the plain individual start of today, no preview, no question. The
    // finish-member warning stays: finishing is exactly what the mode asks for before the next start.
    const kind = withholdBatchStartsInSingleJobMode(resolveBatchWarningKind(row, intent), singleJobMode)

    if (kind === BATCH_WARNING_KIND.RESUME_BATCH || kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setBatchWarning({ open: true, kind, row, statusCode, intent })
      return
    }

    if (kind === BATCH_WARNING_KIND.GROUP_SHORTCUT) {
      previewBatchStart(buildGroupShortcutRequest(row)).then((response) => {
        if (hasEnoughGroupMembersToOfferShortcut(response?.startableCount)) {
          setBatchWarning({ open: true, kind, row, statusCode, intent })
        } else {
          openIndividualStatusChange(row, statusCode, intent)
        }
      })
      return
    }

    openIndividualStatusChange(row, statusCode, intent)
  }, [worker, requestWorker, openIndividualStatusChange, buildGroupShortcutRequest, singleJobMode])

  const handleShowDetails = useCallback((row) => {
    setFoInfoDialog({
      open: true,
      fabricationOrder: row.fabricationOrder,
      initialJob: row.jobReferenceId
    })
  }, [])

  const handleOpenQualityIncidence = useCallback((row) => {
    if (!row?.node || !row?.fabricationOrder) return
    if (!worker) {
      if (requestWorker) requestWorker()
      return
    }
    if (!requestOpenQualityIncidence) return
    requestOpenQualityIncidence({
      node: row.node,
      fabricationOrder: row.fabricationOrder,
      jobReference: { id: row.jobReferenceId },
      device: row.plannedDevice
    })
  }, [requestOpenQualityIncidence, worker, requestWorker])

  const columns = useJobColumns({
    onStatusChange: handleStatusChange,
    onShowDetails: handleShowDetails,
    hiddenColumns: effectiveHiddenColumns,
    onOpenQualityIncidence: handleOpenQualityIncidence,
    batchSelectionEnabled: batchSelectionActive,
    onStartBlocked: handleStartBlocked
  })

  // Historia 120 (enmienda 05, RN8.9) -- the button: disabled with zero marks, its rótulo carrying
  // the count as a plain numeral appended to the EXISTING key (never a second key, never a composed
  // sentence). With exactly one job marked the gesture that follows is the ordinary start of today
  // (RN11/CA1.6/D-I15) -- nothing special is coded for that count here, the SAME preview/mode/submit
  // flow already does the right thing with one pair (S128).
  const handleStartBatchClick = useCallback(() => {
    setBatchPickedRows(batchSelection.getSelectedRows())
    setBatchPickedPairs(batchSelection.selectedPairs)
    setBatchPreviewOpen(true)
  }, [batchSelection])

  // Story 96 (S12/RN7/RN10): no default sort is sent any more. The main list is fed by one of the five
  // shared sources (whichever `primaryList` resolves to, #59) and their order is now the SERVER's:
  // queue priority asc → unprioritised last → start date asc. `SmartMateriaUITable` seeds
  // `sortColumnFieldPageable` from `sortOptions.default`, so passing one shipped `sortKey:['startDate']`
  // on the first request and the date beat the new order. An explicit header click by the operator
  // still wins -- that is not a default (RN10).

  if (error) {
    return (
      <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
        <Typography variant='h6' color='error'>{error.message}</Typography>
      </Stack>
    )
  }

  if (preview) {
    // In preview mode, we don't fetch data and just show simple table
    return (
      <div style={{ flexGrow: 1, overflow: 'hidden' }}>
        <MosSmartTable
          columns={columns}
          dataFetch={() => Promise.resolve([])}
          tablePageSize={10}
          showSearchHeader
          pageable
        />
      </div>
    )
  }
  const handleSelectNode = (node, fabricationOrderInfo, jobReference) => {
    selectNode(node, fabricationOrderInfo, jobReference)
  }

  const onSuccess = async () => {
    setReloadFlag((prev) => prev + 1)
    // S115: a start -- in EITHER mode -- leaves the job selected on the station. The station must
    // never be left with no selected job because the operator chose preparation.
    if (!selectsJobOnSuccess(statusChangeDialogInfo.intent)) return
    const { node, fabricationOrderInfo, jobReference } = await changeDialogInfo(statusChangeDialogInfo)
    handleSelectNode(node, fabricationOrderInfo, jobReference)
  }

  // D-I10: ONE mounted preview/mode dialog, shared by the picker flow (P5, `batchPreviewOpen`) and the
  // row-level group-shortcut/batch-resume flow (P6, `rowBatchFlow`) -- never both at once, only one
  // gesture can be in flight. Whichever is open decides the request/handlers; when neither is, this
  // degrades to exactly P5's own values (no regression, `startBatchFlow` suite).
  const activePreviewOpen = batchPreviewOpen || rowBatchFlow.open
  const activePreviewRequest = rowBatchFlow.open ? rowBatchFlow.request : batchPreviewRequest
  const activePreviewClose = rowBatchFlow.open ? handleRowBatchPreviewClose : handleBatchPreviewClose
  const activePreviewConfirm = rowBatchFlow.open ? handleRowBatchPreviewConfirm : handleBatchPreviewConfirm
  const activeModeOpen = batchModeOpen || rowBatchModeOpen
  const activeModeCancel = rowBatchModeOpen ? handleRowBatchModeCancel : handleBatchModeCancel
  const activeModeConfirm = rowBatchModeOpen ? handleRowBatchModeConfirm : handleBatchModeConfirm

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <SingleJobModeBlockedSnackbar open={startBlockedOpen} onClose={closeStartBlocked} />
      {/* Header */}
      {!preview && (
        // pt: this widget's provider sets hideHeader: true, so WidgetBase renders us directly
        // inside its own `overflow: auto` content div with no top padding -- PeriodSelect's
        // floating label (SmartSelect always renders it shrunk, sitting a few px above the
        // input's own border) would otherwise be clipped right at that boundary. The "Show all"
        // modal doesn't need this: its dialog content already has generous padding/margin above
        // the same select. Local to this widget only -- WidgetBase itself is shared by every
        // widget and is not touched, and the select's own look is unchanged.
        <Stack direction='row' gap={1} alignItems='center' sx={{ width: '100%', pt: '0.75rem' }}>
          <Paper elevation={6} style={styles.logo}>
            <ListIcon style={{ margin: '10%', width: '85%', height: '85%' }} color='primary' />
          </Paper>
          <Typography variant='h6'>{t_('Planned Jobs')}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <ComponentSelect value={componentCode} options={componentOptions} onChange={setComponentCode} />
          <PeriodSelect value={period} onChange={setPeriod} />
          {/* Historia 120 (enmienda 05, D-SP22 point 1/S106/S108/S227) -- the button lives ONLY in
              this header, next to "Show all", and ONLY when the main list is the planned queue
              (S108): with any other primary list configured there is no selection to act on. */}
          {batchSelectionActive && (
            <WorkerRequiredButton
              variant='outlined'
              disabled={batchSelection.selectedCount === 0}
              onClick={handleStartBatchClick}
            >
              {batchSelection.selectedCount > 0 ? `${t_('Start batch')} (${batchSelection.selectedCount})` : t_('Start batch')}
            </WorkerRequiredButton>
          )}
          <Button variant='contained' onClick={() => setOpenCustomModal(true)}>
            {t_('Show all')}
          </Button>
        </Stack>
      )}

      {/* Content */}
      <div style={{ flexGrow: 1, overflow: 'auto' }}>
        <JobDetailMos
          open={foInfoDialog.open}
          fabricationOrder={foInfoDialog.fabricationOrder}
          setOpen={(open) => setFoInfoDialog((prev) => ({ ...prev, open }))}
          initialJob={foInfoDialog.initialJob}
        />

        <JobStatusChangeDialog
          open={statusChangeDialogInfo.open}
          onCancell={() => setStatusChangeDialogInfo((prev) => ({ ...prev, open: false }))}
          onSuccess={onSuccess}
          jobReference={statusChangeDialogInfo.jobReference}
          node={statusChangeDialogInfo.node}
          fabricationOrder={statusChangeDialogInfo.fabricationOrder}
          statusCode={statusChangeDialogInfo.statusCode}
          intent={statusChangeDialogInfo.intent}
          managePreparation={statusChangeDialogInfo.managePreparation}
        />

        <PlannedJobCustomModal
          open={openCustomModal}
          onClose={() => setOpenCustomModal(false)}
          onJobStarted={handleSelectNode}
          hiddenColumns={effectiveHiddenColumns}
          visibleTabs={effectiveVisibleTabs}
          plannedByTechnologyIds={plannedByTechnologyIds}
          activeTabIndex={modalTabIndex}
          onActiveTabIndexChange={setModalTabIndex}
          componentCode={componentCode}
          onComponentCodeChange={setComponentCode}
        />

        <BatchStartPreviewDialog
          open={activePreviewOpen}
          request={activePreviewRequest}
          onClose={activePreviewClose}
          onConfirm={activePreviewConfirm}
        />

        <BatchStartModeDialog
          open={activeModeOpen}
          onCancel={activeModeCancel}
          onConfirm={activeModeConfirm}
        />

        <BatchStartResultDialog
          open={batchResult.open}
          onClose={handleBatchResultClose}
          result={batchResult.result}
          jobLabels={batchResult.jobLabels}
          rejection={batchResult.rejection}
        />

        {/* Historia 120 (Jupiter, paquete P6) -- S129-S142: the ONE warning, three variants by kind. */}
        <BatchWarningDialog
          open={batchWarning.open}
          kind={batchWarning.kind}
          planningGroup={batchWarning.row?.planningGroup}
          onClose={handleBatchWarningClose}
          onChooseIndividual={handleBatchWarningChooseIndividual}
          onChooseBatch={handleBatchWarningChooseBatch}
        />

        {/* S138: "finish the whole batch" -- N4+N5 as-is, no new route (P8's dialog, unmodified). */}
        <BatchFinishDialog
          open={rowBatchFinish.open}
          deviceId={device?.id}
          workerId={worker?.id}
          batchId={rowBatchFinish.batchId}
          onClose={handleRowBatchFinishClose}
          onFinished={handleRowBatchFinished}
        />

        {showNoTechnologyMessage && (
          <Stack justifyContent='center' alignItems='center' sx={{ flexGrow: 1, p: 4 }}>
            <Typography variant='body1' color='text.secondary'>{t_(NO_TECHNOLOGY_SELECTED_MESSAGE)}</Typography>
          </Stack>
        )}
        {!showNoTechnologyMessage && (
          <MosSmartTable
            columns={columns}
            dataFetch={mainListDataFetch}
            forceLoad={reloadFlag}
            // Story 219 (RN12): another component (or none) reloads the list from its first page.
            resetPageable={componentCode}
            tablePageSize={10}
            showSearchHeader
            pageable
            {...(batchSelectionActive && {
              // Historia 120 (enmienda 05, D-SP22/D-SP23/S114) -- selection lives ONLY on THIS
              // instance, with its OWN synthetic primary key (the row has none of its own,
              // `contrato-sun.md` §3): `generic/SmartMateriaUITable.jsx` is not touched, and no other
              // table of MOS gains a selection column because of this (S227/S229).
              multipleSelection: true,
              primaryKeys: [BATCH_SELECTION_ROW_KEY_FIELD],
              extraFilterParameter: { name: BATCH_SELECTION_NOT_STARTABLE_FIELD, value: true },
              multipleSelectionChange: batchSelection.onMultipleSelectionChange,
              resetMultipleSelectionValue: batchSelection.resetTick,
              // No delete-selection action of this instance's own -- same two props the retired
              // picker already passed to sidestep `generic/SmartMateriaUIToolbar.jsx`'s own delete
              // button, which reaches for `actions.delete` unconditionally otherwise.
              deleteMultipleSelectionDisabled: true,
              multipleSelectionActions: []
            })}
          />
        )}
      </div>
    </div>
  )
}

export default PlannedJobListWidget
